import { ExternalLink } from "lucide-react"
import { useState } from "react"
import type { ComputerSummary } from "../../computers/types"
import { openExternalUrl } from "../../platform/actions"

export function processPortUrl(computer: ComputerSummary, port: number): string | null {
    if (!Number.isInteger(port) || port < 1 || port > 65535) return null
    if (computer.kind === "local") return `http://localhost:${port}/`
    // The relay carries Nest traffic only; its hostname cannot expose a service port.
    for (const route of computer.routes) {
        if (route.type !== "direct") continue
        const url = new URL(route.origin)
        const hostname = url.hostname.toLowerCase().replace(/\.$/, "")
        if (
            hostname === "localhost" ||
            hostname.endsWith(".localhost") ||
            hostname.startsWith("127.") ||
            hostname === "[::1]" ||
            hostname === "0.0.0.0" ||
            hostname === "[::]"
        )
            continue
        return `http://${url.hostname}:${port}/`
    }
    return null
}

export function ProcessPortLink({ computer, port, name }: { computer: ComputerSummary; port?: number; name: string }) {
    const [error, setError] = useState("")
    if (port == null) return null
    const url = processPortUrl(computer, port)
    if (!url) {
        return (
            <span className="shrink-0 text-[10px] text-muted" title={`Port ${port} on ${computer.name}; no direct service address is available`}>
                :{port}
            </span>
        )
    }
    const open = async () => {
        try {
            const result = await openExternalUrl(url)
            setError(result.status === "ok" ? "" : result.status === "failed" ? result.message : result.reason)
        } catch (err) {
            setError(String(err))
        }
    }
    return (
        <span className="flex shrink-0 flex-col items-end">
            <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Open ${name} on port ${port}`}
                title={url}
                className="inline-flex min-h-7 items-center gap-1 px-1 text-[10px] text-primary hover:underline focus-visible:outline focus-visible:outline-primary max-sm:min-h-9"
                onClick={(event) => {
                    event.preventDefault()
                    void open()
                }}
            >
                :{port} <ExternalLink size={11} />
            </a>
            {error ? (
                <span role="alert" className="max-w-36 break-words text-[10px] text-error">
                    {error}
                </span>
            ) : null}
        </span>
    )
}
